// ─────────────────────────────────────────────────────────────────────────────
// Translations: English (en) & Vietnamese (vi)
// ─────────────────────────────────────────────────────────────────────────────

const translations = {
  en: {
    // ── Brand ──────────────────────────────────────────────────────────────
    brand_tagline: "Dive into your performance.",
    brand_description:
      "The official swimmer portal of the Vietnam Aquatics federation. Track results, register for meets, and stay in sync with your club, powered by SportsLink.",
    brand_powered_by: "Powered by SportsLink",
    brand_copyright: "© 2026 Vietnam Aquatics. Powered by SportsLink. All rights reserved.",
    cta_primary: "Register",
    cta_secondary: "Sign In",
    cta_register: "Register",

    // ── Nav links ───────────────────────────────────────────────────────────
    nav_how_it_works: "How it works",
    nav_features: "Features",
    nav_clubs: "Clubs & coaches",
    nav_faq: "FAQ",
    nav_results: "Results",
    nav_close_menu: "Close menu",
    nav_open_menu: "Open menu",
    nav_skip_to_content: "Skip to content",
    nav_dashboard: "Dashboard",
    nav_my_club: "My Club",
    nav_sign_out: "Sign Out",
    nav_delete_account: "Delete Account",
    reg_close: "Close",
    reg_options: "Event Options",
    reg_base_entry: "Base Entry",
    reg_registered: "Registered",
    reg_additional: "Additional Events (Optional)",
    reg_seed_time_na: "N/A",
    reg_confirm: "Confirm & Submit",
    reg_processing: "Processing...",
    reg_all_done: "You are registered for all available options!",
    reg_failed: "Registration failed",

    // ── Hero ────────────────────────────────────────────────────────────────
    hero_eyebrow: "Official Swimmers Portal",
    hero_body:
      "Track your personal bests, register for meets, and stay in sync with your club. The official app of Vietnam Aquatics.",

    // ── How it works ────────────────────────────────────────────────────────
    how_title: "How it works.",
    how_step1_title: "Sign up & pick a club",
    how_step1_desc:
      "Create an account, choose your club, and upload a profile photo and birth certificate.",
    how_step2_title: "Get verified",
    how_step2_desc:
      "Your coach approves club membership while the federation verifies your birth certificate, tracked live, in parallel.",
    how_step3_title: "Unlocked to compete",
    how_step3_desc: "Once both approvals land, you're free to register for any upcoming meet.",
    how_step4_title: "Register for events",
    how_step4_desc: "Pick your sub-events and enter your seed times for each.",
    how_step5_title: "Coach signs off",
    how_step5_desc: "Your coach reviews and approves each event registration.",
    how_step6_title: "Pay & you're in",
    how_step6_desc: "Pay your entry fee securely via Stripe Checkout, then show up and swim.",

    // ── Features ────────────────────────────────────────────────────────────
    features_title: "Everything a season needs.",
    feat1_title: "Digital ID card",
    feat1_body: "A QR-verified Swimmer ID with photo, DOB, blood group and club, downloadable as PDF.",
    feat2_title: "Secure entry-fee payments",
    feat2_body: "Pay meet entry fees through Stripe Checkout, right in the app.",
    feat3_title: "Real-time approval tracking",
    feat3_body: "Watch your club and birth-certificate approvals update live, side by side.",
    feat4_title: "Heat sheets & results",
    feat4_body: "View and download heat sheets and results as PDF for any event.",
    feat5_title: "Personal best tracking",
    feat5_body: "See your best events and podium finishes in one dashboard.",
    feat6_title: "Wearables integration",
    feat6_body: "Sync Apple Health or Google Fit from the Club tab.",
    feat7_title: "Role-based dashboards",
    feat7_body: "Purpose-built views for coaches and federation admins.",
    feat8_title: "Event notifications",
    feat8_body: "Stay on top of approvals, registrations and upcoming meets.",

    // ── FAQ ─────────────────────────────────────────────────────────────────
    faq_title: "Frequently asked.",
    faq1_q: "Who can sign up?",
    faq1_a:
      "Any registered swimmer, coach, or federation admin can sign up. Swimmers choose their club during signup; coach and admin accounts are provisioned by their club or the federation.",
    faq2_q: "How does club/birth-certificate verification work?",
    faq2_a:
      "After signup, your club coach reviews and approves your club membership while the federation admin verifies your birth certificate. Both checks run in parallel, and you can track their status live until both are approved.",
    faq3_q: "How do I pay for event entry?",
    faq3_a:
      "Once your coach approves an event registration, you pay the entry fee securely through Stripe Checkout inside the app.",
    faq4_q: "Is my data secure?",
    faq4_a:
      "Your documents and personal details are stored securely and only shared with your club and the federation for verification and event administration purposes.",
    faq5_q: "Does it work with Apple Health / Google Fit?",
    faq5_a:
      "Yes, swimmers can connect Apple Health or Google Fit from the Club tab to sync activity alongside their swim results.",
    faq6_q: "Is there a coach or admin version?",
    faq6_a:
      "Yes. Coaches get a dashboard for club stats and membership/registration approvals; federation admins get a dashboard for birth-certificate verification and federation-wide stats.",

    // ── Footer ──────────────────────────────────────────────────────────────
    footer_explore: "Explore",
    footer_contact: "Contact",

    // ── Login ───────────────────────────────────────────────────────────────
    login_title: "Sign in",
    login_subtitle: "Welcome back — enter your credentials to continue",
    login_email: "Email address",
    login_password: "Password",
    login_submit: "Sign in",
    login_submitting: "Signing in…",
    login_no_account: "Don't have an account?",
    login_create: "Create one",
    login_home: "Home",
    login_hero_h2: "Dive into excellence.",
    login_hero_span: "Streamline your success.",
    login_hero_p:
      "Join the premier platform dedicated to empowering athletes, coaches, and clubs to reach their full potential.",
    login_err_fill: "Please fill in all fields.",
    login_err_rejected: "Your account registration was rejected. Please contact support.",
    login_err_credentials: "Incorrect email or password. Please try again.",
    login_err_generic: "Something went wrong. Please try again.",
    login_err_network: "Could not connect to the server. Please try again.",
    login_show_password: "Show password",
    login_hide_password: "Hide password",
    login_or: "or",

    // ── Signup ──────────────────────────────────────────────────────────────
    signup_role_swimmer: "Swimmer",
    signup_role_coach: "Coach",
    signup_role_club: "Club",
    signup_step_of: "Step {step} of {total}",
    signup_title_club_1: "Create your account",
    signup_title_club_2: "Review & confirm",
    signup_sub_club_1: "Choose your role and fill in your details",
    signup_sub_club_2: "Double-check everything before submitting",
    signup_title_coach_1: "Create your account",
    signup_title_coach_2: "Pick your club",
    signup_title_coach_3: "Review & confirm",
    signup_sub_coach_1: "Choose your role and tell us who you are",
    signup_sub_coach_2: "Choose the club you'll be coaching at",
    signup_sub_coach_3: "Double-check everything before submitting",
    signup_title_swimmer_1: "Create your account",
    signup_title_swimmer_2: "Set a password",
    signup_title_swimmer_3: "Details & documents",
    signup_title_swimmer_4: "Pick your club",
    signup_title_swimmer_5: "Review & confirm",
    signup_sub_swimmer_1: "Choose your role and tell us who you are",
    signup_sub_swimmer_2: "Secure your account",
    signup_sub_swimmer_3: "Date of birth, blood type, and required documents",
    signup_sub_swimmer_4: "Choose the club you represent, then select your coach",
    signup_sub_swimmer_5: "Double-check everything before submitting",
    signup_next: "Next step",
    signup_back: "Back",
    signup_submit: "Create account",
    signup_creating: "Creating account…",
    signup_have_account: "Already have an account?",
    signup_signin: "Sign in",
    signup_home: "Home",

    // ── Sidebar – Swimmer ────────────────────────────────────────────────────
    sidebar_swimmer_portal: "Swimmer Portal",
    sidebar_dashboard: "Dashboard",
    sidebar_workouts: "My Workouts",
    sidebar_competitions: "Competitions",
    sidebar_events: "Events",
    sidebar_results: "My Results",
    sidebar_goals: "Goals",
    sidebar_profile_section: "Profile",
    sidebar_id_card: "My ID Card",
    sidebar_sign_out: "Sign Out",
    sidebar_delete_account: "Delete Account",

    // ── Sidebar – Coach ──────────────────────────────────────────────────────
    sidebar_coach_portal: "Coach Portal",
    sidebar_athletes: "Athletes",
    sidebar_verification: "Verifications",
    sidebar_coach_results: "Results",

    // ── Sidebar – Admin ──────────────────────────────────────────────────────
    sidebar_admin_portal: "Admin Portal",
    sidebar_admin_dashboard: "Dashboard",
    sidebar_admin_clubs: "Clubs",
    sidebar_admin_events: "Events",
    sidebar_admin_database: "Database",
    sidebar_admin_results: "Results Ingestion",

    // ── Swimmer Dashboard ─────────────────────────────────────────────────────
    dash_title: "My Dashboard",
    dash_subtitle: "Here's your swimming progress this week.",
    dash_pending_notice:
      "Your account is waiting for coach approval. You can still update your birth certificate or profile photo from",
    dash_pending_profile: "your profile",
    dash_rejected_notice:
      "Your club membership request was rejected. Please choose your club and coach again and resubmit your documents from",
    dash_approved: "You've been approved! Welcome to the team.",
    dash_weekly_distance: "Weekly Distance",
    dash_streak: "Current Streak",
    dash_workouts: "workouts",
    dash_best_event: "Best Event",
    dash_sessions: "Sessions",
    dash_coach_notes: "Recent Notes from Coach",
    dash_recently: "Recently",
    dash_upload_fail_both: "Your birth certificate and profile photo couldn't be uploaded during signup.",
    dash_upload_fail_cert: "Your birth certificate couldn't be uploaded during signup.",
    dash_upload_fail_photo: "Your profile photo couldn't be uploaded during signup.",
    dash_upload_fail_add_them: "Please add them from",
    dash_upload_fail_add_it: "Please add it from",
    dash_upload_fail_profile_link: "your profile",

    // ── Swimmer Profile / ID Card ─────────────────────────────────────────────
    profile_title: "My ID Card",
    profile_subtitle: "Your official Vietnam Aquatics swimmer identity card.",
    profile_tap_flip: "Tap to flip",
    profile_download: "Download ID Card",
    profile_personal: "Personal Information",
    profile_full_name: "Full Name",
    profile_email: "Email",
    profile_phone: "Phone",
    profile_dob: "Date of Birth",
    profile_blood: "Blood Group",
    profile_role: "Role",
    profile_role_swimmer: "Swimmer",
    profile_club_coaching: "Club & Coaching",
    profile_club: "Club",
    profile_coach: "Assigned Coach",
    profile_status: "Account Status",
    profile_swimmer_id: "Swimmer ID",
    profile_official_id: "Official Identity",
    profile_official_desc:
      "This card certifies your membership with Vietnam Aquatics. Present this ID at official events for verification.",
    profile_unaffiliated: "Unaffiliated",
    profile_pending_title: "Awaiting coach approval",
    profile_rejected_title: "Membership rejected",
    profile_select_club: "Select club…",
    profile_select_coach: "Select coach…",
    profile_resubmit: "Resubmit membership request",
    profile_docs_fine: "Your documents are on file. Nothing more to do here.",
    profile_update_docs_rejected: "You can also update your birth certificate or profile photo.",
    profile_update_docs_pending: "You can update your birth certificate or profile photo while your account is pending.",
    profile_birth_cert: "Birth certificate",
    profile_photo: "Profile photo",
    profile_verified: "Verified",
    profile_submitted: "Submitted — awaiting review",
    profile_uploaded: "Uploaded",
    profile_view: "View",
    profile_hide: "Hide",
    profile_save: "Save updates",

    // ── Swimmer Events ─────────────────────────────────────────────────────────
    events_title: "Events",
    events_subtitle: "Browse upcoming competitions and register for events.",
    events_empty: "No upcoming events at the moment.",
    events_empty_hint: "Check back soon for upcoming competitions.",
    events_register: "Register",
    events_registered: "Registered",
    events_view: "View Details",
    events_organised: "Organised by Vietnam Aquatics",
    events_register_by: "Register by:",
    events_num: "events",
    events_fully_registered: "Fully Registered",
    events_add_events: "Already Registered — Add Events",
    events_register_now: "Register Now",
    events_closed: "Registration Closed",

    // ── Swimmer Results ────────────────────────────────────────────────────────
    results_title: "My Results",
    results_subtitle: "Your competition history and personal bests.",
    results_empty: "No results yet.",
    results_empty_hint: "Your competition results will appear here once events are recorded.",
    results_event: "Event",
    results_time: "Time",
    results_place: "Place",
    results_date: "Date",
    results_category: "Category",
    results_status: "Status",
    results_my_rank: "My Rank",
    results_my_time: "My Time",
    results_field_size: "Field Size",
    results_swimmers: "swimmers",
    results_top5: "Top 5 Leaderboard",
    results_you: "You",

    // ── Swimmer Goals ──────────────────────────────────────────────────────────
    goals_title: "My Goals",
    goals_subtitle: "Set and track personal milestones.",
    goals_add: "New Goal",
    goals_empty: "No goals set yet",
    goals_empty_hint: "Set a personal milestone to start tracking your progress.",
    goals_completed: "Completed",
    goals_current: "Current",
    goals_target: "Target",
    goals_progress: "Progress",
    goals_create: "Create Goal",
    goals_title_label: "Title",
    goals_icon_label: "Icon",
    goals_cancel: "Cancel",
    goals_save: "Save Goal",
    goals_reached: "Goal Reached!",
    goals_reached_msg: "You've hit 100% on",
    goals_reached_msg2: "Would you like to mark this goal as completed?",
    goals_yes: "Yes, mark as completed!",
    goals_not_yet: "Not yet",
    goals_delete: "Delete",

    // ── Swimmer Workouts ───────────────────────────────────────────────────────
    workouts_title: "My Workouts",
    workouts_subtitle: "Tasks assigned by your coach.",
    workouts_empty: "No workouts assigned yet",
    workouts_empty_hint: "Your coach will add sessions here as they plan your training.",
    workouts_mins: "mins",
    workouts_add: "Log Workout",

    // ── Verification Pending ───────────────────────────────────────────────────
    vp_title: "Account Pending",
    vp_subtitle: "Your account is under review.",
    vp_body:
      "Your registration has been submitted. Your coach and the federation are reviewing your documents. You'll be notified once approved.",
    vp_sign_out: "Sign Out",

    // ── Coach Dashboard ────────────────────────────────────────────────────────
    coach_dash_title: "Coach Dashboard",
    coach_dash_subtitle: "Welcome back! Here's an overview of your team.",
    coach_dash_pending_verif: "Pending Verifications",
    coach_dash_pending_members: "Pending Memberships",
    coach_dash_pending_regs: "Event Registrations",
    coach_dash_team_stats: "Team Stats",
    coach_dash_approved_members: "Approved Members",
    coach_dash_approved_regs: "Approved Registrations",

    // ── Coach Athletes ─────────────────────────────────────────────────────────
    coach_athletes_title: "Athletes Roster",
    coach_athletes_subtitle: "Manage your swimmers, assign workouts, and send notes.",
    coach_athletes_view_goals: "View Goals",
    coach_athletes_assign: "Assign Tasks",
    coach_athletes_send_note: "Send Note",
    coach_athletes_search: "Search by name or ID...",
    coach_athletes_selected: "selected",
    coach_athletes_col_swimmer: "Swimmer",
    coach_athletes_col_age: "Age Group",
    coach_athletes_col_dob: "DOB",
    coach_athletes_empty: "No swimmers found",
    coach_athletes_empty_hint1: "Try a different name or ID.",
    coach_athletes_empty_hint2: "Approved swimmers assigned to you will show up here.",
    coach_athletes_modal_task: "Assign Workout Task",
    coach_athletes_modal_note: "Send Note",
    coach_athletes_assigning: "Assigning to",
    coach_athletes_swimmers: "swimmer(s).",
    coach_athletes_task_details: "Task Details",
    coach_athletes_duration: "Duration (Minutes)",
    coach_athletes_msg_content: "Message Content",
    coach_athletes_write_note: "Write a note...",
    coach_athletes_confirm_send: "Confirm Send",
    coach_athletes_goals_title: "Swimmer Goals",
    coach_athletes_goals_sub: "Tracking progress for selected swimmer.",
    coach_athletes_no_goals: "No goals found",
    coach_athletes_no_goals_hint: "This swimmer hasn't set any goals yet.",
    coach_athletes_cur: "Cur:",
    coach_athletes_tgt: "Tgt:",
    coach_athletes_done: "Done",

    // ── Coach Verification ─────────────────────────────────────────────────────
    coach_verif_title: "Verifications",
    coach_verif_subtitle: "Review and approve pending swimmer requests and birth certificates.",
    coach_approve: "Approve",
    coach_reject: "Reject",
    coach_pending: "Pending",
    coach_approved: "Approved",
    coach_rejected: "Rejected",
    coach_verif_pending_swimmers: "Pending Swimmer Verifications",
    coach_verif_mem_pending: "Membership Pending",
    coach_verif_cert_pending: "Certificate Pending",
    coach_verif_hide: "Hide",
    coach_verif_view: "View",
    coach_verif_err_load: "Could not load certificate.",
    coach_verif_empty: "No pending verifications",
    coach_verif_reject_swimmer: "Reject Swimmer",
    coach_verif_reject_reason: "Reason for rejection",
    coach_verif_reject_hint: "e.g. Document is blurry, please re-upload",
    coach_verif_confirm_reject: "Confirm Reject",

    // ── Coach Results ──────────────────────────────────────────────────────────
    coach_results_title: "Team Results",
    coach_results_subtitle: "View performance metrics and event results for your team.",
    coach_results_search: "Search by event or swimmer name...",
    coach_results_col_rank: "Rank",
    coach_results_col_swimmer: "Swimmer",
    coach_results_col_event: "Event",
    coach_results_col_cat: "Category",
    coach_results_col_time: "Time",
    coach_results_empty: "No results found",
    coach_results_empty_hint1: "Try a different event or swimmer name.",
    coach_results_empty_hint2: "Results will appear here once events are recorded.",

    // ── Admin Dashboard ────────────────────────────────────────────────────────
    admin_dash_title: "Platform Overview",
    admin_dash_subtitle: "Key metrics and financial overview across the entire association.",
    admin_dash_verified_swimmers: "Verified Swimmers",
    admin_dash_active_clubs: "Active Clubs",
    admin_dash_total_events: "Total Events",
    admin_dash_total_revenue: "Total Revenue",
    admin_dash_pending: "pending",
    admin_dash_7d_revenue: "7-Day Revenue",
    admin_dash_top_clubs: "Top Clubs",
    admin_dash_members: "members",
    admin_dash_no_clubs: "No clubs data yet.",
    admin_dash_biggest_events: "Biggest Events",
    admin_dash_registrations: "registrations",
    admin_dash_no_regs: "No registrations yet.",

    // ── Admin Clubs ────────────────────────────────────────────────────────────
    admin_clubs_title: "Pending Club Approvals",
    admin_clubs_subtitle: "Review and verify new Club registrations. Coaches are verified by the Club, Swimmers by the Coach.",
    admin_clubs_refresh: "Refresh",
    admin_clubs_verifies: "Admin verifies",
    admin_clubs_label: "Clubs",
    admin_clubs_caught_up: "All caught up!",
    admin_clubs_no_pending: "There are no pending Club registrations to review.",
    admin_clubs_col_name: "Club Name",
    admin_clubs_col_admin: "Admin / Applicant",
    admin_clubs_col_loc: "Location",
    admin_clubs_col_date: "Date Applied",
    admin_clubs_reject: "Reject",
    admin_clubs_approve: "Approve",
    admin_clubs_err_auth: "You are not authorised to view this page. Please log in as an admin.",
    admin_clubs_err_conn: "Could not connect to the server.",
    admin_clubs_err_action: "Action failed. Please try again.",

    // ── Admin Events ───────────────────────────────────────────────────────────
    admin_events_title: "Events Management",
    admin_events_subtitle: "Create, edit, or remove association swimming events.",
    admin_events_new: "New Event",
    admin_events_reg_open: "Reg: Open",
    admin_events_reg_closed: "Reg: Closed",
    admin_events_edit: "Edit",
    admin_events_view_regs: "View Registrants",
    admin_events_modal_edit: "Edit Event",
    admin_events_modal_create: "Create Event",
    admin_events_basic_details: "Basic Details",
    admin_events_title_label: "Event Title",
    admin_events_venue: "Venue",
    admin_events_start_date: "Start Date",
    admin_events_end_date: "End Date",
    admin_events_pricing_poster: "Pricing & Poster",
    admin_events_base_fee: "Base Entry Fee ($)",
    admin_events_poster: "Event Poster",
    admin_events_change: "Change",
    admin_events_remove: "Remove",
    admin_events_upload_poster: "Upload poster image",
    admin_events_fallback_color: "Fallback color, used if no poster image is set.",
    admin_events_classification: "Classification",
    admin_events_age_group: "Age Group",
    admin_events_main_cat: "Main Category",
    admin_events_events_list: "Events",
    admin_events_name: "Event Name",
    admin_events_price: "Price ($)",
    admin_events_add: "Add",
    admin_events_save: "Save Event",
    admin_events_delete_title: "Delete this event?",
    admin_events_delete_msg1: '"',
    admin_events_delete_msg2: '" and all its registrations will be permanently deleted.',
    admin_events_delete_btn: "Delete Event",

    // ── Admin Database ─────────────────────────────────────────────────────────
    admin_db_title: "Database",
    admin_db_subtitle: "Full read/write view of every table in the shared database. Password fields are never returned or editable.",
    admin_db_search_placeholder: "Search",
    admin_db_search_btn: "Search",
    admin_db_clear_filters: "Clear filters",
    admin_db_export_csv: "Export CSV",
    admin_db_col_actions: "Actions",
    admin_db_empty_filtered: "No rows match your search or filters.",
    admin_db_empty_table: "This table is empty.",
    admin_db_showing: "Showing",
    admin_db_of: "of",
    admin_db_page: "Page",
    admin_db_delete_title: "Delete this row?",
    admin_db_delete_msg: "This row will be permanently deleted and cannot be restored.",
    admin_db_delete_btn: "Delete Row",
    admin_db_contains: "Contains…",
    admin_db_apply: "Apply",
    admin_db_clear: "Clear",

    // ── Club Admin Dashboard ───────────────────────────────────────────────────
    club_admin_dash_welcome: "Welcome back!",
    club_admin_dash_overview: "Here's your club overview.",
    club_admin_dash_pending_coaches: "Pending Coach Approvals",
    club_admin_dash_no_pending: "No pending coaches.",
    club_admin_dash_applied: "Applied recently",
    club_admin_dash_approve: "Approve",

    // ── Club Admin My Club ─────────────────────────────────────────────────────
    club_admin_my_club_title: "My Club",
    club_admin_my_club_subtitle: "Here's your club roster.",
    club_admin_my_club_coaches: "Coaches",
    club_admin_my_club_coach_count: "coach(es)",
    club_admin_my_club_loading: "Loading your club roster...",
    club_admin_my_club_no_coaches: "No coaches in your club yet.",
    club_admin_my_club_swimmer_count: "swimmer(s)",
    club_admin_my_club_no_swimmers: "No swimmers assigned to this coach yet.",
    club_admin_my_club_no_details: "No details",
    club_admin_my_club_approved: "Approved",

    // ── Live Results ───────────────────────────────────────────────────────────
    live_results_title: "Live Results",
    live_results_home: "Home",
    live_results_pending_event: "Results will appear once an event is completed.",
    live_results_pending_race: "Results will appear once this event is completed.",
    live_results_all_genders: "All genders",
    live_results_all_strokes: "All strokes",
    live_results_all_ages: "All ages",
    live_results_search: "Search athlete or team…",
    live_results_no_match: "No results match your filters.",
    live_results_col_rank: "Rank",
    live_results_col_athlete: "Athlete",
    live_results_col_team: "Team",
    live_results_col_time: "Time",
    stroke_freestyle: "Freestyle",
    stroke_backstroke: "Backstroke",
    stroke_breaststroke: "Breaststroke",
    stroke_butterfly: "Butterfly",
    stroke_medley: "Medley",
    stroke_relay: "Relay",
    stroke_other: "Other",

    // ── Admin Results Ingestion ────────────────────────────────────────────────
    admin_ingest_title: "Results Ingestion",
    admin_ingest_subtitle: "Pick the event already on the site, upload its HY-TEK results PDF, review/correct each race, then publish.",
    admin_ingest_step1_title: "1. Choose the event",
    admin_ingest_step1_desc: "Results are published under an event already on the site.",
    admin_ingest_select_event: "Select an event…",
    admin_ingest_step2_title: "2. Upload results PDF",
    admin_ingest_step2_desc1: "Text-based HY-TEK Meet Manager export (not a scanned image).",
    admin_ingest_step2_desc2: "Choose an event above first.",
    admin_ingest_parsing: "Parsing…",
    admin_ingest_choose_pdf: "Choose Results PDF",
    admin_ingest_step3_title: "3. Review parsed results",
    admin_ingest_publish_btn: "Publish Event",
    admin_ingest_col_rank: "Rank",
    admin_ingest_col_athlete: "Athlete",
    admin_ingest_col_age: "Age",
    admin_ingest_col_team: "Team",
    admin_ingest_col_time: "Time",
    admin_ingest_add_row: "Add Swimmer Row",
    admin_ingest_pub_title: "Published results",
    admin_ingest_col_event: "Event",
    admin_ingest_col_race: "Race",
    admin_ingest_col_gender: "Gender",
    admin_ingest_col_results: "Results",
    admin_ingest_no_results: "No results published yet.",
    admin_ingest_delete_title: "Delete published results?",
    admin_ingest_delete_msg1: 'All published results for "',
    admin_ingest_delete_msg2: '" will be permanently deleted.',
    admin_ingest_delete_btn: "Delete Results",
    admin_ingest_toast_parsed: "Parsed {count} result(s) — review and publish below.",
    admin_ingest_toast_published: 'Published "{race}" under {event}.',
    admin_ingest_fallback_event: "the selected event",
    admin_ingest_results_count: "result(s)",

    // ── Common ─────────────────────────────────────────────────────────────────
    common_loading: "Loading…",
    common_error: "An error occurred. Please try again.",
    common_save: "Save",
    common_cancel: "Cancel",
    common_delete: "Delete",
    common_confirm: "Confirm",
    common_close: "Close",
    common_search: "Search",
    common_name: "Name",
    common_email: "Email",
    common_status: "Status",
    common_actions: "Actions",
    common_yes: "Yes",
    common_no: "No",
    common_back: "Back",
    common_submit: "Submit",
    common_or: "or",
    common_unaffiliated: "Unaffiliated",
    common_na: "—",

    // ── Delete Account Modal ───────────────────────────────────────────────────
    delete_title: "Delete Account",
    delete_warning: "This action is permanent and cannot be undone. All your data will be erased.",
    delete_confirm_label: "Type DELETE to confirm",
    delete_confirm_placeholder: "DELETE",
    delete_btn: "Delete My Account",
    delete_cancel: "Cancel",

    // ── Confirm Modal ──────────────────────────────────────────────────────────
    confirm_title: "Confirm Action",
    confirm_yes: "Yes, confirm",
    confirm_no: "Cancel",

    // ── Registration Modal ─────────────────────────────────────────────────────
    reg_title: "Register for Event",
    reg_select_events: "Select Sub-events",
    reg_seed_time: "Seed Time",
    reg_submit: "Submit Registration",
    reg_cancel: "Cancel",
  },

  vi: {
    // ── Brand ──────────────────────────────────────────────────────────────
    brand_tagline: "Nâng cao thành tích bơi lội của bạn.",
    brand_description:
      "Cổng thông tin chính thức dành cho vận động viên bơi lội của Liên đoàn Bơi lội Việt Nam. Theo dõi kết quả, đăng ký thi đấu và kết nối với câu lạc bộ của bạn, được cung cấp bởi SportsLink.",
    brand_powered_by: "Được cung cấp bởi SportsLink",
    brand_copyright: "© 2026 Liên đoàn Bơi lội Việt Nam. Được cung cấp bởi SportsLink. Bảo lưu mọi quyền.",
    cta_primary: "Đăng ký",
    cta_secondary: "Đăng nhập",
    cta_register: "Đăng ký",

    // ── Nav links ───────────────────────────────────────────────────────────
    nav_how_it_works: "Cách thức hoạt động",
    nav_features: "Tính năng",
    nav_clubs: "CLB & HLV",
    nav_faq: "Câu hỏi thường gặp",
    nav_results: "Kết quả",
    nav_close_menu: "Đóng menu",
    nav_open_menu: "Mở menu",
    nav_skip_to_content: "Chuyển tới nội dung",
    nav_dashboard: "Tổng quan",
    nav_my_club: "Câu lạc bộ của tôi",
    nav_sign_out: "Đăng xuất",
    nav_delete_account: "Xóa tài khoản",
    reg_close: "Đóng",
    reg_options: "Tùy chọn sự kiện",
    reg_base_entry: "Đăng ký cơ bản",
    reg_registered: "Đã đăng ký",
    reg_additional: "Nội dung thi bổ sung (không bắt buộc)",
    reg_seed_time_na: "Chưa có",
    reg_confirm: "Xác nhận & Gửi",
    reg_processing: "Đang xử lý...",
    reg_all_done: "Bạn đã đăng ký tất cả nội dung có thể!",
    reg_failed: "Đăng ký không thành công",

    // ── Hero ────────────────────────────────────────────────────────────────
    hero_eyebrow: "Cổng Thông Tin Vận Động Viên Chính Thức",
    hero_body:
      "Theo dõi thành tích cá nhân tốt nhất, đăng ký thi đấu và kết nối với câu lạc bộ. Ứng dụng chính thức của Liên đoàn Bơi lội Việt Nam.",

    // ── How it works ────────────────────────────────────────────────────────
    how_title: "Cách thức hoạt động.",
    how_step1_title: "Đăng ký & chọn CLB",
    how_step1_desc:
      "Tạo tài khoản, chọn câu lạc bộ và tải lên ảnh đại diện cùng giấy khai sinh.",
    how_step2_title: "Xác minh tài khoản",
    how_step2_desc:
      "Huấn luyện viên của bạn phê duyệt tư cách thành viên CLB trong khi liên đoàn xác minh giấy khai sinh, được theo dõi trực tiếp song song.",
    how_step3_title: "Mở khóa thi đấu",
    how_step3_desc: "Sau khi cả hai phê duyệt hoàn tất, bạn có thể đăng ký tham dự bất kỳ giải đấu nào.",
    how_step4_title: "Đăng ký nội dung thi đấu",
    how_step4_desc: "Chọn các nội dung bơi và nhập thành tích tham chiếu cho từng nội dung.",
    how_step5_title: "HLV xác nhận",
    how_step5_desc: "Huấn luyện viên xem xét và phê duyệt từng đăng ký thi đấu.",
    how_step6_title: "Thanh toán & sẵn sàng",
    how_step6_desc: "Thanh toán phí đăng ký an toàn qua Stripe Checkout, rồi có mặt và thi đấu.",

    // ── Features ────────────────────────────────────────────────────────────
    features_title: "Mọi thứ bạn cần cho một mùa giải.",
    feat1_title: "Thẻ vận động viên số",
    feat1_body: "Thẻ căn cước VĐV có mã QR, ảnh, ngày sinh, nhóm máu và CLB, có thể tải xuống dưới dạng PDF.",
    feat2_title: "Thanh toán phí thi đấu an toàn",
    feat2_body: "Thanh toán phí đăng ký qua Stripe Checkout ngay trong ứng dụng.",
    feat3_title: "Theo dõi phê duyệt theo thời gian thực",
    feat3_body: "Xem trạng thái phê duyệt CLB và giấy khai sinh cập nhật trực tiếp.",
    feat4_title: "Bảng xếp hạng & kết quả",
    feat4_body: "Xem và tải xuống bảng phân lượt và kết quả thi đấu dưới dạng PDF.",
    feat5_title: "Theo dõi thành tích cá nhân tốt nhất",
    feat5_body: "Xem các nội dung tốt nhất và thứ hạng huy chương trong một bảng điều khiển.",
    feat6_title: "Tích hợp thiết bị đeo",
    feat6_body: "Đồng bộ Apple Health hoặc Google Fit từ tab CLB.",
    feat7_title: "Bảng điều khiển theo vai trò",
    feat7_body: "Giao diện chuyên biệt cho HLV và quản trị viên liên đoàn.",
    feat8_title: "Thông báo sự kiện",
    feat8_body: "Cập nhật mọi phê duyệt, đăng ký và giải đấu sắp tới.",

    // ── FAQ ─────────────────────────────────────────────────────────────────
    faq_title: "Câu hỏi thường gặp.",
    faq1_q: "Ai có thể đăng ký?",
    faq1_a:
      "Bất kỳ vận động viên, huấn luyện viên hoặc quản trị viên liên đoàn nào đều có thể đăng ký. VĐV chọn CLB trong quá trình đăng ký; tài khoản HLV và quản trị viên được cấp bởi CLB hoặc liên đoàn.",
    faq2_q: "Xác minh CLB/giấy khai sinh hoạt động như thế nào?",
    faq2_a:
      "Sau khi đăng ký, HLV CLB xem xét và phê duyệt tư cách thành viên trong khi quản trị viên liên đoàn xác minh giấy khai sinh. Cả hai quy trình chạy song song và bạn có thể theo dõi trạng thái trực tiếp cho đến khi được phê duyệt.",
    faq3_q: "Làm thế nào để thanh toán phí đăng ký thi đấu?",
    faq3_a:
      "Sau khi HLV phê duyệt đăng ký thi đấu, bạn thanh toán phí an toàn qua Stripe Checkout trong ứng dụng.",
    faq4_q: "Dữ liệu của tôi có được bảo mật không?",
    faq4_a:
      "Tài liệu và thông tin cá nhân của bạn được lưu trữ an toàn và chỉ được chia sẻ với CLB và liên đoàn để xác minh và quản lý thi đấu.",
    faq5_q: "Ứng dụng có hoạt động với Apple Health / Google Fit không?",
    faq5_a:
      "Có, VĐV có thể kết nối Apple Health hoặc Google Fit từ tab CLB để đồng bộ hoạt động cùng với kết quả bơi lội.",
    faq6_q: "Có phiên bản dành cho HLV hoặc quản trị viên không?",
    faq6_a:
      "Có. HLV có bảng điều khiển riêng để xem thống kê CLB và phê duyệt thành viên/đăng ký; quản trị viên liên đoàn có bảng điều khiển để xác minh giấy khai sinh và xem thống kê toàn liên đoàn.",

    // ── Footer ──────────────────────────────────────────────────────────────
    footer_explore: "Khám phá",
    footer_contact: "Liên hệ",

    // ── Login ───────────────────────────────────────────────────────────────
    login_title: "Đăng nhập",
    login_subtitle: "Chào mừng trở lại — nhập thông tin đăng nhập để tiếp tục",
    login_email: "Địa chỉ email",
    login_password: "Mật khẩu",
    login_submit: "Đăng nhập",
    login_submitting: "Đang đăng nhập…",
    login_no_account: "Chưa có tài khoản?",
    login_create: "Tạo tài khoản",
    login_home: "Trang chủ",
    login_hero_h2: "Chạm tới đỉnh cao.",
    login_hero_span: "Tối ưu hóa thành công của bạn.",
    login_hero_p:
      "Tham gia nền tảng hàng đầu dành riêng để trao quyền cho vận động viên, huấn luyện viên và câu lạc bộ phát huy tối đa tiềm năng.",
    login_err_fill: "Vui lòng điền đầy đủ thông tin.",
    login_err_rejected: "Tài khoản của bạn đã bị từ chối. Vui lòng liên hệ hỗ trợ.",
    login_err_credentials: "Email hoặc mật khẩu không chính xác. Vui lòng thử lại.",
    login_err_generic: "Đã xảy ra lỗi. Vui lòng thử lại.",
    login_err_network: "Không thể kết nối đến máy chủ. Vui lòng thử lại.",
    login_show_password: "Hiện mật khẩu",
    login_hide_password: "Ẩn mật khẩu",
    login_or: "hoặc",

    // ── Signup ──────────────────────────────────────────────────────────────
    signup_role_swimmer: "Vận động viên",
    signup_role_coach: "Huấn luyện viên",
    signup_role_club: "Câu lạc bộ",
    signup_step_of: "Bước {step} / {total}",
    signup_title_club_1: "Tạo tài khoản của bạn",
    signup_title_club_2: "Xem lại & xác nhận",
    signup_sub_club_1: "Chọn vai trò và điền thông tin chi tiết của bạn",
    signup_sub_club_2: "Kiểm tra kỹ mọi thứ trước khi gửi",
    signup_title_coach_1: "Tạo tài khoản của bạn",
    signup_title_coach_2: "Chọn câu lạc bộ của bạn",
    signup_title_coach_3: "Xem lại & xác nhận",
    signup_sub_coach_1: "Chọn vai trò và cho chúng tôi biết bạn là ai",
    signup_sub_coach_2: "Chọn câu lạc bộ bạn sẽ huấn luyện",
    signup_sub_coach_3: "Kiểm tra kỹ mọi thứ trước khi gửi",
    signup_title_swimmer_1: "Tạo tài khoản của bạn",
    signup_title_swimmer_2: "Đặt mật khẩu",
    signup_title_swimmer_3: "Chi tiết & tài liệu",
    signup_title_swimmer_4: "Chọn câu lạc bộ của bạn",
    signup_title_swimmer_5: "Xem lại & xác nhận",
    signup_sub_swimmer_1: "Chọn vai trò và cho chúng tôi biết bạn là ai",
    signup_sub_swimmer_2: "Bảo mật tài khoản của bạn",
    signup_sub_swimmer_3: "Ngày sinh, nhóm máu và các tài liệu bắt buộc",
    signup_sub_swimmer_4: "Chọn câu lạc bộ bạn đại diện, sau đó chọn HLV của bạn",
    signup_sub_swimmer_5: "Kiểm tra kỹ mọi thứ trước khi gửi",
    signup_next: "Bước tiếp theo",
    signup_back: "Quay lại",
    signup_submit: "Tạo tài khoản",
    signup_creating: "Đang tạo tài khoản…",
    signup_have_account: "Đã có tài khoản?",
    signup_signin: "Đăng nhập",
    signup_home: "Trang chủ",

    // ── Sidebar – Swimmer ────────────────────────────────────────────────────
    sidebar_swimmer_portal: "Cổng VĐV",
    sidebar_dashboard: "Tổng quan",
    sidebar_workouts: "Buổi tập của tôi",
    sidebar_competitions: "Thi đấu",
    sidebar_events: "Giải đấu",
    sidebar_results: "Kết quả của tôi",
    sidebar_goals: "Mục tiêu",
    sidebar_profile_section: "Hồ sơ",
    sidebar_id_card: "Thẻ VĐV",
    sidebar_sign_out: "Đăng xuất",
    sidebar_delete_account: "Xóa tài khoản",

    // ── Sidebar – Coach ──────────────────────────────────────────────────────
    sidebar_coach_portal: "Cổng HLV",
    sidebar_athletes: "Vận động viên",
    sidebar_verification: "Xác minh",
    sidebar_coach_results: "Kết quả",

    // ── Sidebar – Admin ──────────────────────────────────────────────────────
    sidebar_admin_portal: "Cổng Quản trị",
    sidebar_admin_dashboard: "Tổng quan",
    sidebar_admin_clubs: "Câu lạc bộ",
    sidebar_admin_events: "Giải đấu",
    sidebar_admin_database: "Cơ sở dữ liệu",
    sidebar_admin_results: "Nhập kết quả",

    // ── Swimmer Dashboard ─────────────────────────────────────────────────────
    dash_title: "Bảng điều khiển",
    dash_subtitle: "Tiến trình bơi lội của bạn trong tuần này.",
    dash_pending_notice:
      "Tài khoản của bạn đang chờ HLV phê duyệt. Bạn vẫn có thể cập nhật giấy khai sinh hoặc ảnh đại diện từ",
    dash_pending_profile: "hồ sơ của bạn",
    dash_rejected_notice:
      "Yêu cầu tham gia CLB của bạn đã bị từ chối. Vui lòng chọn lại CLB và HLV rồi nộp lại tài liệu từ",
    dash_approved: "Bạn đã được phê duyệt! Chào mừng bạn đến với đội!",
    dash_weekly_distance: "Quãng đường tuần",
    dash_streak: "Chuỗi hiện tại",
    dash_workouts: "buổi tập",
    dash_best_event: "Nội dung xuất sắc nhất",
    dash_sessions: "Buổi tập",
    dash_coach_notes: "Ghi chú của HLV",
    dash_recently: "Gần đây",
    dash_upload_fail_both: "Không thể tải lên giấy khai sinh và ảnh đại diện của bạn trong quá trình đăng ký.",
    dash_upload_fail_cert: "Không thể tải lên giấy khai sinh của bạn trong quá trình đăng ký.",
    dash_upload_fail_photo: "Không thể tải lên ảnh đại diện của bạn trong quá trình đăng ký.",
    dash_upload_fail_add_them: "Vui lòng thêm chúng từ",
    dash_upload_fail_add_it: "Vui lòng thêm tài liệu từ",
    dash_upload_fail_profile_link: "hồ sơ của bạn",

    // ── Swimmer Profile / ID Card ─────────────────────────────────────────────
    profile_title: "Thẻ VĐV",
    profile_subtitle: "Thẻ căn cước vận động viên chính thức của Liên đoàn Bơi lội Việt Nam.",
    profile_tap_flip: "Chạm để lật",
    profile_download: "Tải thẻ VĐV",
    profile_personal: "Thông tin cá nhân",
    profile_full_name: "Họ và tên",
    profile_email: "Email",
    profile_phone: "Số điện thoại",
    profile_dob: "Ngày sinh",
    profile_blood: "Nhóm máu",
    profile_role: "Vai trò",
    profile_role_swimmer: "Vận động viên",
    profile_club_coaching: "CLB & Huấn luyện",
    profile_club: "Câu lạc bộ",
    profile_coach: "Huấn luyện viên phụ trách",
    profile_status: "Trạng thái tài khoản",
    profile_swimmer_id: "Mã VĐV",
    profile_official_id: "Danh tính chính thức",
    profile_official_desc:
      "Thẻ này xác nhận tư cách thành viên của bạn với Liên đoàn Bơi lội Việt Nam. Xuất trình thẻ tại các sự kiện chính thức để xác minh.",
    profile_unaffiliated: "Chưa liên kết CLB",
    profile_pending_title: "Chờ HLV phê duyệt",
    profile_rejected_title: "Tư cách thành viên bị từ chối",
    profile_select_club: "Chọn câu lạc bộ…",
    profile_select_coach: "Chọn huấn luyện viên…",
    profile_resubmit: "Gửi lại yêu cầu thành viên",
    profile_docs_fine: "Tài liệu của bạn đã có trong hệ thống. Không cần làm thêm gì.",
    profile_update_docs_rejected: "Bạn cũng có thể cập nhật giấy khai sinh hoặc ảnh đại diện.",
    profile_update_docs_pending: "Bạn có thể cập nhật giấy khai sinh hoặc ảnh đại diện trong khi tài khoản đang chờ duyệt.",
    profile_birth_cert: "Giấy khai sinh",
    profile_photo: "Ảnh đại diện",
    profile_verified: "Đã xác minh",
    profile_submitted: "Đã nộp — đang chờ xem xét",
    profile_uploaded: "Đã tải lên",
    profile_view: "Xem",
    profile_hide: "Ẩn",
    profile_save: "Lưu cập nhật",

    // ── Swimmer Events ─────────────────────────────────────────────────────────
    events_title: "Giải đấu",
    events_subtitle: "Duyệt các giải đấu sắp tới và đăng ký.",
    events_empty: "Hiện chưa có giải đấu nào sắp diễn ra.",
    events_empty_hint: "Vui lòng quay lại sau.",
    events_register: "Đăng ký",
    events_registered: "Đã đăng ký",
    events_view: "Xem chi tiết",
    events_organised: "Tổ chức bởi Liên đoàn Bơi lội Việt Nam",
    events_register_by: "Hạn chót:",
    events_num: "nội dung",
    events_fully_registered: "Đã đăng ký đủ",
    events_add_events: "Đã đăng ký — Thêm nội dung",
    events_register_now: "Đăng ký ngay",
    events_closed: "Đã đóng đăng ký",

    // ── Swimmer Results ────────────────────────────────────────────────────────
    results_title: "Kết quả của tôi",
    results_subtitle: "Lịch sử thi đấu và thành tích cá nhân tốt nhất của bạn.",
    results_empty: "Chưa có kết quả nào.",
    results_empty_hint: "Kết quả thi đấu của bạn sẽ xuất hiện ở đây khi giải đấu kết thúc.",
    results_event: "Nội dung",
    results_time: "Thời gian",
    results_place: "Thứ hạng",
    results_date: "Ngày",
    results_category: "Nhóm",
    results_status: "Trạng thái",
    results_my_rank: "Thứ hạng của tôi",
    results_my_time: "Thành tích",
    results_field_size: "Số lượng tham gia",
    results_swimmers: "VĐV",
    results_top5: "Top 5 chung cuộc",
    results_you: "Bạn",

    // ── Swimmer Goals ──────────────────────────────────────────────────────────
    goals_title: "Mục tiêu của tôi",
    goals_subtitle: "Đặt và theo dõi các cột mốc cá nhân.",
    goals_add: "Mục tiêu mới",
    goals_empty: "Chưa có mục tiêu nào",
    goals_empty_hint: "Đặt một cột mốc cá nhân để bắt đầu theo dõi tiến độ của bạn.",
    goals_completed: "Hoàn thành",
    goals_current: "Hiện tại",
    goals_target: "Mục tiêu",
    goals_progress: "Tiến độ",
    goals_create: "Tạo mục tiêu",
    goals_title_label: "Tiêu đề",
    goals_icon_label: "Biểu tượng",
    goals_cancel: "Hủy",
    goals_save: "Lưu mục tiêu",
    goals_reached: "Đã đạt mục tiêu!",
    goals_reached_msg: "Bạn đã đạt 100% cho",
    goals_reached_msg2: "Bạn có muốn đánh dấu mục tiêu này là đã hoàn thành không?",
    goals_yes: "Có, đánh dấu hoàn thành!",
    goals_not_yet: "Chưa",
    goals_delete: "Xóa",

    // ── Swimmer Workouts ───────────────────────────────────────────────────────
    workouts_title: "Buổi tập của tôi",
    workouts_subtitle: "Các bài tập do HLV giao.",
    workouts_empty: "Chưa có buổi tập nào.",
    workouts_empty_hint: "HLV của bạn sẽ thêm các buổi tập vào đây khi họ lên kế hoạch.",
    workouts_mins: "phút",
    workouts_add: "Ghi buổi tập",

    // ── Verification Pending ───────────────────────────────────────────────────
    vp_title: "Tài khoản đang chờ duyệt",
    vp_subtitle: "Tài khoản của bạn đang được xem xét.",
    vp_body:
      "Đăng ký của bạn đã được gửi đi. HLV và liên đoàn đang xem xét tài liệu của bạn. Bạn sẽ được thông báo khi được phê duyệt.",
    vp_sign_out: "Đăng xuất",

    // ── Coach Dashboard ────────────────────────────────────────────────────────
    coach_dash_title: "Bảng điều khiển HLV",
    coach_dash_subtitle: "Chào mừng trở lại! Dưới đây là tổng quan về đội của bạn.",
    coach_dash_pending_verif: "Xác minh đang chờ",
    coach_dash_pending_members: "Thành viên đang chờ duyệt",
    coach_dash_pending_regs: "Đăng ký thi đấu đang chờ",
    coach_dash_team_stats: "Thống kê Đội",
    coach_dash_approved_members: "Thành viên đã duyệt",
    coach_dash_approved_regs: "Đăng ký đã duyệt",

    // ── Coach Athletes ─────────────────────────────────────────────────────────
    coach_athletes_title: "Danh sách VĐV",
    coach_athletes_subtitle: "Quản lý VĐV của bạn, giao bài tập và gửi ghi chú.",
    coach_athletes_view_goals: "Xem mục tiêu",
    coach_athletes_assign: "Giao bài tập",
    coach_athletes_send_note: "Gửi ghi chú",
    coach_athletes_search: "Tìm kiếm theo tên hoặc ID...",
    coach_athletes_selected: "đã chọn",
    coach_athletes_col_swimmer: "VĐV",
    coach_athletes_col_age: "Nhóm tuổi",
    coach_athletes_col_dob: "Ngày sinh",
    coach_athletes_empty: "Không tìm thấy VĐV",
    coach_athletes_empty_hint1: "Thử tìm kiếm tên hoặc ID khác.",
    coach_athletes_empty_hint2: "Các VĐV được duyệt cho bạn sẽ hiển thị ở đây.",
    coach_athletes_modal_task: "Giao bài tập",
    coach_athletes_modal_note: "Gửi ghi chú",
    coach_athletes_assigning: "Đang giao cho",
    coach_athletes_swimmers: "VĐV.",
    coach_athletes_task_details: "Chi tiết bài tập",
    coach_athletes_duration: "Thời lượng (Phút)",
    coach_athletes_msg_content: "Nội dung tin nhắn",
    coach_athletes_write_note: "Viết ghi chú...",
    coach_athletes_confirm_send: "Xác nhận Gửi",
    coach_athletes_goals_title: "Mục tiêu VĐV",
    coach_athletes_goals_sub: "Theo dõi tiến độ của VĐV đã chọn.",
    coach_athletes_no_goals: "Không tìm thấy mục tiêu",
    coach_athletes_no_goals_hint: "VĐV này chưa đặt mục tiêu nào.",
    coach_athletes_cur: "HT:",
    coach_athletes_tgt: "MT:",
    coach_athletes_done: "Hoàn thành",

    // ── Coach Verification ─────────────────────────────────────────────────────
    coach_verif_title: "Xác minh",
    coach_verif_subtitle: "Xem xét và phê duyệt các yêu cầu tham gia và giấy khai sinh của VĐV.",
    coach_approve: "Phê duyệt",
    coach_reject: "Từ chối",
    coach_pending: "Đang chờ",
    coach_approved: "Đã phê duyệt",
    coach_rejected: "Đã từ chối",
    coach_verif_pending_swimmers: "VĐV đang chờ xác minh",
    coach_verif_mem_pending: "Đang chờ duyệt thành viên",
    coach_verif_cert_pending: "Đang chờ duyệt giấy khai sinh",
    coach_verif_hide: "Ẩn",
    coach_verif_view: "Xem",
    coach_verif_err_load: "Không thể tải giấy khai sinh.",
    coach_verif_empty: "Không có yêu cầu xác minh nào",
    coach_verif_reject_swimmer: "Từ chối VĐV",
    coach_verif_reject_reason: "Lý do từ chối",
    coach_verif_reject_hint: "Ví dụ: Tài liệu bị mờ, vui lòng tải lên lại",
    coach_verif_confirm_reject: "Xác nhận từ chối",

    // ── Coach Results ──────────────────────────────────────────────────────────
    coach_results_title: "Kết quả của Đội",
    coach_results_subtitle: "Xem số liệu hiệu suất và kết quả thi đấu của đội bạn.",
    coach_results_search: "Tìm kiếm theo sự kiện hoặc tên VĐV...",
    coach_results_col_rank: "Xếp hạng",
    coach_results_col_swimmer: "VĐV",
    coach_results_col_event: "Sự kiện",
    coach_results_col_cat: "Hạng mục",
    coach_results_col_time: "Thời gian",
    coach_results_empty: "Không tìm thấy kết quả",
    coach_results_empty_hint1: "Thử tìm kiếm tên sự kiện hoặc VĐV khác.",
    coach_results_empty_hint2: "Kết quả sẽ hiển thị ở đây khi các sự kiện được ghi nhận.",

    // ── Admin Dashboard ────────────────────────────────────────────────────────
    admin_dash_title: "Tổng quan nền tảng",
    admin_dash_subtitle: "Các chỉ số chính và tổng quan tài chính của toàn bộ hiệp hội.",
    admin_dash_verified_swimmers: "VĐV đã xác minh",
    admin_dash_active_clubs: "CLB hoạt động",
    admin_dash_total_events: "Tổng sự kiện",
    admin_dash_total_revenue: "Tổng doanh thu",
    admin_dash_pending: "đang chờ",
    admin_dash_7d_revenue: "Doanh thu 7 ngày",
    admin_dash_top_clubs: "Các CLB hàng đầu",
    admin_dash_members: "thành viên",
    admin_dash_no_clubs: "Chưa có dữ liệu CLB.",
    admin_dash_biggest_events: "Sự kiện lớn nhất",
    admin_dash_registrations: "lượt đăng ký",
    admin_dash_no_regs: "Chưa có lượt đăng ký nào.",

    // ── Admin Clubs ────────────────────────────────────────────────────────────
    admin_clubs_title: "Phê duyệt CLB đang chờ",
    admin_clubs_subtitle: "Xem xét và xác minh đăng ký CLB mới. HLV được CLB xác minh, VĐV được HLV xác minh.",
    admin_clubs_refresh: "Làm mới",
    admin_clubs_verifies: "Quản trị viên xác minh",
    admin_clubs_label: "Các CLB",
    admin_clubs_caught_up: "Đã hoàn thành!",
    admin_clubs_no_pending: "Không có đăng ký CLB nào đang chờ xem xét.",
    admin_clubs_col_name: "Tên CLB",
    admin_clubs_col_admin: "Quản trị / Người nộp",
    admin_clubs_col_loc: "Vị trí",
    admin_clubs_col_date: "Ngày nộp",
    admin_clubs_reject: "Từ chối",
    admin_clubs_approve: "Phê duyệt",
    admin_clubs_err_auth: "Bạn không được phép xem trang này. Vui lòng đăng nhập với tư cách quản trị viên.",
    admin_clubs_err_conn: "Không thể kết nối đến máy chủ.",
    admin_clubs_err_action: "Hành động thất bại. Vui lòng thử lại.",

    // ── Admin Events ───────────────────────────────────────────────────────────
    admin_events_title: "Quản lý sự kiện",
    admin_events_subtitle: "Tạo, chỉnh sửa hoặc xóa các sự kiện bơi lội của liên đoàn.",
    admin_events_new: "Sự kiện mới",
    admin_events_reg_open: "Đăng ký: Mở",
    admin_events_reg_closed: "Đăng ký: Đóng",
    admin_events_edit: "Sửa",
    admin_events_view_regs: "Xem lượt đăng ký",
    admin_events_modal_edit: "Chỉnh sửa sự kiện",
    admin_events_modal_create: "Tạo sự kiện",
    admin_events_basic_details: "Chi tiết cơ bản",
    admin_events_title_label: "Tên sự kiện",
    admin_events_venue: "Địa điểm",
    admin_events_start_date: "Ngày bắt đầu",
    admin_events_end_date: "Ngày kết thúc",
    admin_events_pricing_poster: "Giá & Áp phích",
    admin_events_base_fee: "Phí đăng ký cơ bản ($)",
    admin_events_poster: "Áp phích sự kiện",
    admin_events_change: "Thay đổi",
    admin_events_remove: "Xóa bỏ",
    admin_events_upload_poster: "Tải ảnh áp phích lên",
    admin_events_fallback_color: "Màu dự phòng, dùng nếu không có áp phích.",
    admin_events_classification: "Phân loại",
    admin_events_age_group: "Nhóm tuổi",
    admin_events_main_cat: "Hạng mục chính",
    admin_events_events_list: "Các nội dung thi đấu",
    admin_events_name: "Tên nội dung",
    admin_events_price: "Giá ($)",
    admin_events_add: "Thêm",
    admin_events_save: "Lưu sự kiện",
    admin_events_delete_title: "Xóa sự kiện này?",
    admin_events_delete_msg1: '"',
    admin_events_delete_msg2: '" và tất cả lượt đăng ký của nó sẽ bị xóa vĩnh viễn.',
    admin_events_delete_btn: "Xóa sự kiện",

    // ── Admin Database ─────────────────────────────────────────────────────────
    admin_db_title: "Cơ sở dữ liệu",
    admin_db_subtitle: "Dạng xem đọc/ghi toàn bộ bảng trong cơ sở dữ liệu dùng chung. Trường mật khẩu không bao giờ được trả về hoặc chỉnh sửa.",
    admin_db_search_placeholder: "Tìm kiếm",
    admin_db_search_btn: "Tìm kiếm",
    admin_db_clear_filters: "Xóa bộ lọc",
    admin_db_export_csv: "Xuất CSV",
    admin_db_col_actions: "Hành động",
    admin_db_empty_filtered: "Không có hàng nào khớp với tìm kiếm hoặc bộ lọc của bạn.",
    admin_db_empty_table: "Bảng này trống.",
    admin_db_showing: "Đang hiển thị",
    admin_db_of: "trong",
    admin_db_page: "Trang",
    admin_db_delete_title: "Xóa hàng này?",
    admin_db_delete_msg: "Hàng này sẽ bị xóa vĩnh viễn và không thể khôi phục.",
    admin_db_delete_btn: "Xóa Hàng",
    admin_db_contains: "Chứa...",
    admin_db_apply: "Áp dụng",
    admin_db_clear: "Xóa",

    // ── Club Admin Dashboard ───────────────────────────────────────────────────
    club_admin_dash_welcome: "Chào mừng trở lại!",
    club_admin_dash_overview: "Đây là tổng quan về câu lạc bộ của bạn.",
    club_admin_dash_pending_coaches: "HLV đang chờ duyệt",
    club_admin_dash_no_pending: "Không có HLV nào đang chờ.",
    club_admin_dash_applied: "Mới nộp gần đây",
    club_admin_dash_approve: "Phê duyệt",

    // ── Club Admin My Club ─────────────────────────────────────────────────────
    club_admin_my_club_title: "Câu lạc bộ của tôi",
    club_admin_my_club_subtitle: "Đây là danh sách thành viên câu lạc bộ của bạn.",
    club_admin_my_club_coaches: "Huấn luyện viên",
    club_admin_my_club_coach_count: "HLV",
    club_admin_my_club_loading: "Đang tải danh sách thành viên...",
    club_admin_my_club_no_coaches: "Chưa có huấn luyện viên nào trong câu lạc bộ của bạn.",
    club_admin_my_club_swimmer_count: "VĐV",
    club_admin_my_club_no_swimmers: "Chưa có vận động viên nào được phân công cho huấn luyện viên này.",
    club_admin_my_club_no_details: "Không có thông tin chi tiết",
    club_admin_my_club_approved: "Đã duyệt",

    // ── Live Results ───────────────────────────────────────────────────────────
    live_results_title: "Kết quả Trực tiếp",
    live_results_home: "Trang chủ",
    live_results_pending_event: "Kết quả sẽ xuất hiện sau khi một sự kiện kết thúc.",
    live_results_pending_race: "Kết quả sẽ xuất hiện sau khi vòng đua này kết thúc.",
    live_results_all_genders: "Tất cả giới tính",
    live_results_all_strokes: "Tất cả kiểu bơi",
    live_results_all_ages: "Tất cả độ tuổi",
    live_results_search: "Tìm kiếm vận động viên hoặc đội...",
    live_results_no_match: "Không có kết quả nào khớp với bộ lọc.",
    live_results_col_rank: "Hạng",
    live_results_col_athlete: "VĐV",
    live_results_col_team: "Đội",
    live_results_col_time: "Thời gian",
    stroke_freestyle: "Bơi tự do",
    stroke_backstroke: "Bơi ngửa",
    stroke_breaststroke: "Bơi ếch",
    stroke_butterfly: "Bơi bướm",
    stroke_medley: "Bơi hỗn hợp",
    stroke_relay: "Bơi tiếp sức",
    stroke_other: "Khác",

    // ── Admin Results Ingestion ────────────────────────────────────────────────
    admin_ingest_title: "Nhập kết quả",
    admin_ingest_subtitle: "Chọn sự kiện đã có trên trang web, tải lên tệp PDF kết quả HY-TEK, xem xét/chỉnh sửa từng vòng đua, sau đó xuất bản.",
    admin_ingest_step1_title: "1. Chọn sự kiện",
    admin_ingest_step1_desc: "Kết quả được xuất bản theo một sự kiện đã có trên trang web.",
    admin_ingest_select_event: "Chọn một sự kiện...",
    admin_ingest_step2_title: "2. Tải lên tệp PDF kết quả",
    admin_ingest_step2_desc1: "Bản xuất dạng văn bản từ HY-TEK Meet Manager (không phải ảnh quét).",
    admin_ingest_step2_desc2: "Vui lòng chọn một sự kiện ở trên trước.",
    admin_ingest_parsing: "Đang phân tích...",
    admin_ingest_choose_pdf: "Chọn tệp PDF kết quả",
    admin_ingest_step3_title: "3. Xem xét kết quả đã phân tích",
    admin_ingest_publish_btn: "Xuất bản sự kiện",
    admin_ingest_col_rank: "Hạng",
    admin_ingest_col_athlete: "VĐV",
    admin_ingest_col_age: "Tuổi",
    admin_ingest_col_team: "Đội",
    admin_ingest_col_time: "Thời gian",
    admin_ingest_add_row: "Thêm VĐV",
    admin_ingest_pub_title: "Kết quả đã xuất bản",
    admin_ingest_col_event: "Sự kiện",
    admin_ingest_col_race: "Cuộc đua",
    admin_ingest_col_gender: "Giới tính",
    admin_ingest_col_results: "Kết quả",
    admin_ingest_no_results: "Chưa có kết quả nào được xuất bản.",
    admin_ingest_delete_title: "Xóa kết quả đã xuất bản?",
    admin_ingest_delete_msg1: 'Tất cả kết quả đã xuất bản cho "',
    admin_ingest_delete_msg2: '" sẽ bị xóa vĩnh viễn.',
    admin_ingest_delete_btn: "Xóa kết quả",
    admin_ingest_toast_parsed: "Đã phân tích {count} kết quả — vui lòng xem lại và xuất bản bên dưới.",
    admin_ingest_toast_published: 'Đã xuất bản "{race}" dưới "{event}".',
    admin_ingest_fallback_event: "sự kiện đã chọn",
    admin_ingest_results_count: "kết quả",

    // ── Common ─────────────────────────────────────────────────────────────────
    common_loading: "Đang tải…",
    common_error: "Đã xảy ra lỗi. Vui lòng thử lại.",
    common_save: "Lưu",
    common_cancel: "Hủy",
    common_delete: "Xóa",
    common_confirm: "Xác nhận",
    common_close: "Đóng",
    common_search: "Tìm kiếm",
    common_name: "Tên",
    common_email: "Email",
    common_status: "Trạng thái",
    common_actions: "Thao tác",
    common_yes: "Có",
    common_no: "Không",
    common_back: "Quay lại",
    common_submit: "Gửi",
    common_or: "hoặc",
    common_unaffiliated: "Chưa liên kết CLB",
    common_na: "—",

    // ── Delete Account Modal ───────────────────────────────────────────────────
    delete_title: "Xóa tài khoản",
    delete_warning: "Hành động này là vĩnh viễn và không thể hoàn tác. Tất cả dữ liệu của bạn sẽ bị xóa.",
    delete_confirm_label: "Nhập XÓA để xác nhận",
    delete_confirm_placeholder: "XÓA",
    delete_btn: "Xóa tài khoản của tôi",
    delete_cancel: "Hủy",

    // ── Confirm Modal ──────────────────────────────────────────────────────────
    confirm_title: "Xác nhận thao tác",
    confirm_yes: "Có, xác nhận",
    confirm_no: "Hủy",

    // ── Registration Modal ─────────────────────────────────────────────────────
    reg_title: "Đăng ký thi đấu",
    reg_select_events: "Chọn nội dung thi đấu",
    reg_seed_time: "Thành tích tham chiếu",
    reg_submit: "Gửi đăng ký",
    reg_cancel: "Hủy",
  },
}

export default translations
